<template>
    <div class="quiz-editor">
        <Loader v-if="isLoading" />
        <div
            v-else
            class="quiz-editor__layout"
            data-aos="fade-left"
            data-aos-duration="1500"
        >
            <div class="quiz-editor__canvas">
                <QuizEditorHeader
                    :is-editing="isEditing"
                    :question-count="questions.length"
                    :alternative-count="currentAlternativeCount"
                    :required-score="requiredScore"
                    :approval-percentage="approvalPercentage"
                    @back="openCancelModal"
                />

                <QuizGeneralSettings
                    :title="quiz.title"
                    :required-score="requiredScore"
                    :approval-percentage="approvalPercentage"
                    :title-error="fieldErrors.title"
                    @update:title="quiz.title = $event"
                    @update:required-score="requiredScore = $event"
                    @update:approval-percentage="approvalPercentage = $event"
                />

                <section class="quiz-editor__questions">
                    <header class="quiz-section-head">
                        <span class="quiz-section-head__icon" aria-hidden="true">
                            <fa icon="fa-solid fa-list" />
                        </span>
                        <div>
                            <h2 class="quiz-section-head__title">
                                {{ $t('admin.quiz.questionsSection') }}
                            </h2>
                            <p class="quiz-section-head__hint">
                                {{ $t('admin.quiz.questionsHint') }}
                            </p>
                        </div>
                    </header>

                    <draggable
                        v-if="questions && questions.length"
                        v-model="questions"
                        class="quiz-questions"
                        ghost-class="ghost"
                        tag="ul"
                        v-bind="dragOptions"
                        :item-key="questionItemKey"
                        @start="drag = true"
                        @end="onDragEnd"
                        handle=".question-card__handle-icon"
                    >
                        <template #item="{ element, index }">
                            <QuizQuestionCard
                                :key="questionItemKey(element)"
                                :question="element"
                                :index="index"
                                :opened="!!openedQuestions[index]"
                                :can-add-answer="!maxAnswersReached(index)"
                                :errors="fieldErrors.questions[index]"
                                @toggle="toggleQuestionCard(index)"
                                @activate="activeQuestionIndex = index"
                                @remove="openDeleteQuestionModal(index)"
                                @select-correct="handleCorrectAnswer(index, $event)"
                                @add-answer="addNewAnswer(index)"
                                @remove-answer="removeAnswer(index, $event)"
                                @fill-sample="fillQuestionSample(index)"
                            />
                        </template>
                    </draggable>

                    <p v-else class="quiz-editor__empty">
                        {{ $t('admin.quiz.emptyQuestions') }}
                    </p>

                    <div v-show="!maxQuestionsReached()" class="quiz-editor__add-wrap">
                        <button
                            type="button"
                            class="quiz-editor__add-question"
                            @click="addNewQuestion()"
                        >
                            <fa icon="fa-solid fa-plus" />
                            <span>{{ $t('admin.quiz.addQuestionBtn') }}</span>
                        </button>
                    </div>
                </section>
            </div>

            <QuizBottomBar :saving="isSaving" @save="saveAll" />
        </div>

        <v-dialog
            v-model="deleteQuestionModal"
            class="delete-question-dialog"
            max-width="500px"
            transition="fade"
        >
            <v-card class="delete-question-dialog__card pa-3 pa-md-5">
                <v-card-title class="px-0"> {{ $t('admin.quiz.deleteQuestionTitle') }} </v-card-title>
                <div class="px-0">
                    <p>
                        Tem certeza de que deseja excluir a
                        <strong>{{ $t('admin.quiz.questionLabel') }} {{ deleteQuestionIndex + 1 }}</strong
                        >?
                    </p>
                </div>
                <v-card-actions class="px-0 justify-content-end">
                    <v-btn color="" @click="deleteQuestionModal = false"
                        >Cancelar</v-btn
                    >
                    <v-btn
                        variant="danger"
                        color="danger"
                        @click="removeQuestion()"
                        >Sim, excluir</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="cancelModal"
            class="cancel-quiz-dialog"
            max-width="500px"
            transition="fade"
        >
            <v-card class="cancel-quiz-dialog__card pa-3 pa-md-5">
                <v-card-title class="px-0"> {{ $t('admin.quiz.leavePageTitle') }} </v-card-title>
                <div class="px-0">
                    <p>
                        Tem certeza de que deseja sair da página?
                        <strong
                            >{{ $t('admin.quiz.leavePageText') }}</strong
                        >
                    </p>
                </div>
                <v-card-actions class="px-0 justify-content-end">
                    <v-btn color="" @click="cancelModal = false">{{ $t('common.no') }}</v-btn>
                    <v-btn
                        variant="danger"
                        color="danger"
                        @click="$router.push('/dashboard/curso')"
                        >Sim, voltar</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import draggable from 'vuedraggable';
    import Loader from '@/components/Loader/loader.vue';
    import QuizEditorHeader from '@/components/Admin/Quiz/Editor/QuizEditorHeader.vue';
    import QuizGeneralSettings from '@/components/Admin/Quiz/Editor/QuizGeneralSettings.vue';
    import QuizQuestionCard from '@/components/Admin/Quiz/Editor/QuizQuestionCard.vue';
    import QuizBottomBar from '@/components/Admin/Quiz/Editor/QuizBottomBar.vue';

    import apiQuiz from '@/utils/quiz';
    import apiAnswer from '@/utils/answer';
    import apiLesson from '@/utils/lesson';
    import { toast } from 'vue3-toastify';
    import AOS from 'aos';

    const questionKeyMap = new WeakMap();
    let questionKeySeq = 0;

    function questionItemKey(question) {
        if (question && question.id != null && question.id !== '') {
            return `id-${question.id}`;
        }
        if (!questionKeyMap.has(question)) {
            questionKeySeq += 1;
            questionKeyMap.set(question, `local-${questionKeySeq}`);
        }
        return questionKeyMap.get(question);
    }

    export default {
        name: 'CreateQuiz',
        components: {
            draggable,
            Loader,
            QuizEditorHeader,
            QuizGeneralSettings,
            QuizQuestionCard,
            QuizBottomBar,
        },
        data() {
            return {
                lessonData: null,
                quiz: {
                    title: '',
                    minimum: 0,
                    maximum: 10,
                },
                requiredScore: false,
                approvalPercentage: 70,
                questions: [
                    {
                        description: '',
                        resolutionComment: '',
                        order: 0,
                        status: 1,
                        answers: [
                            {
                                description: '',
                                correct: 1,
                            },
                        ],
                    },
                ],
                openedQuestions: [true],
                maxQuestions: 400,
                maxAnswers: 5,
                isLoading: false,
                drag: false,
                deleteQuestionIndex: null,
                deleteQuestionModal: false,
                cancelModal: false,
                isSaving: false,
                activeQuestionIndex: 0,
                fieldErrors: {
                    title: '',
                    questions: [],
                },
                hasUnsavedChanges: false,
                initialFormState: null,
                isInitialized: false,
                isReloading: false,
            };
        },
        computed: {
            ...mapState(['token', 'currentModule', 'currentCollections']),
            isEditing() {
                return !!this.$route.params.id;
            },
            lessonId() {
                return this.$route.params.id || null;
            },
            dragOptions() {
                return {
                    animation: 200,
                    group: 'description',
                    disabled: false,
                    ghostClass: 'ghost',
                };
            },
            currentAlternativeCount() {
                const question = this.questions[this.activeQuestionIndex];
                if (!question || !Array.isArray(question.answers)) return 0;
                return question.answers.length;
            },
        },
        watch: {
            quiz: {
                handler() {
                    if (
                        this.fieldErrors.title &&
                        this.quiz.title &&
                        this.quiz.title.trim()
                    ) {
                        this.fieldErrors.title = '';
                    }
                    if (this.isInitialized && !this.hasUnsavedChanges) {
                        this.checkForChanges();
                    }
                },
                deep: true,
            },
            requiredScore(newVal) {
                this.quiz.minimum = newVal ? this.approvalPercentage / 10 : 0;
                if (this.isInitialized && !this.hasUnsavedChanges) {
                    this.checkForChanges();
                }
            },
            approvalPercentage(newVal, oldVal) {
                this.approvalPercentage = Math.round(newVal);
                if (newVal < 0 || newVal > 100) {
                    this.approvalPercentage = oldVal;
                }
                this.quiz.minimum = this.approvalPercentage / 10;
                if (this.isInitialized && !this.hasUnsavedChanges) {
                    this.checkForChanges();
                }
            },
            questions: {
                handler() {
                    this.pruneQuestionErrors();
                    if (this.isInitialized && !this.hasUnsavedChanges) {
                        this.checkForChanges();
                    }
                },
                deep: true,
            },
        },
        beforeMount() {
            if (this.isEditing) {
                this.getAllData();
            } else {
                this.$nextTick(() => {
                    this.isInitialized = true;
                    this.saveInitialState();
                });
            }
        },
        mounted() {
            this.$nextTick(() => {
                AOS.refresh();
            });
        },
        methods: {
            questionItemKey,
            async getAllData() {
                this.isLoading = true;
                this.isInitialized = false;

                try {
                    if (this.lessonId) {
                        await this.getLessonData(this.lessonId);
                    }
                } catch (error) {
                    this.notify(this.$t('admin.quiz.loadError'), 'error');
                    console.error('Error fetching data:', error);
                } finally {
                    this.isLoading = false;
                    this.$nextTick(() => {
                        this.saveInitialState();
                        this.isInitialized = true;
                    });
                }
            },
            async getLessonData(lessonId) {
                try {
                    const lesson = await apiLesson.getDataLessonById(
                        lessonId,
                        this.token
                    );
                    this.lessonData = lesson;
                    await this.getQuizData(lesson.idQuiz);
                } catch (error) {
                    console.error('Error fetching lesson data:', error);
                }
            },
            async getQuizData(idQuiz) {
                if (!idQuiz) {
                    this.isLoading = false;
                    return;
                }
                try {
                    const quiz = await apiQuiz.getDataQuizById(
                        idQuiz,
                        this.token
                    );
                    this.quiz = quiz;
                    this.requiredScore = quiz.minimum > 0;
                    this.approvalPercentage = quiz.minimum * 10;
                    await this.getQuestionsByQuiz(idQuiz);
                } catch (error) {
                    console.error('Error fetching quiz data:', error);
                }
            },
            async getQuestionsByQuiz(idQuiz) {
                try {
                    const response = await apiAnswer.getQuestionsByQuiz(
                        idQuiz,
                        this.token
                    );
                    // Clear existing questions
                    this.questions.splice(0, this.questions.length);
                    // Add fetched questions
                    if (Array.isArray(response)) {
                        this.questions.push(
                            ...response.map((question) => ({
                                ...question,
                                resolutionComment: question.resolutionComment || '',
                            })),
                        );
                    }
                    // Mark the first question as opened
                    this.openedQuestions = this.questions.map(
                        (_, idx) => idx === 0
                    );
                } catch (error) {
                    console.error('Error fetching questions:', error);
                }
            },
            toggleQuestionCard(index) {
                this.openedQuestions[index] = !this.openedQuestions[index];
                this.activeQuestionIndex = index;
            },
            onDragEnd() {
                this.drag = false;
                const lastIndex = this.questions.length - 1;
                if (this.activeQuestionIndex > lastIndex) {
                    this.activeQuestionIndex = Math.max(lastIndex, 0);
                }
            },
            pruneQuestionErrors() {
                if (!this.fieldErrors.questions.length) return;
                this.fieldErrors.questions.forEach((error, qIdx) => {
                    if (!error) return;
                    const question = this.questions[qIdx];
                    if (!question) return;
                    const statement = (question.description || '').replace(/<[^>]+>/g, '').trim();
                    if (error.description && statement) {
                        error.description = '';
                    }
                    if (error.answers && question.answers && question.answers.length >= 1) {
                        error.answers = '';
                    }
                    if (error.correct && question.answers?.some((answer) => answer.correct)) {
                        error.correct = '';
                    }
                    if (error.items) {
                        Object.keys(error.items).forEach((key) => {
                            const answer = question.answers?.[key];
                            if (answer && String(answer.description || '').trim()) {
                                delete error.items[key];
                            }
                        });
                    }
                });
            },
            handleCorrectAnswer(questionIndex, answerIndex) {
                const answers = this.questions[questionIndex].answers;
                answers.forEach((answer, idx) => {
                    answer.correct = idx === answerIndex ? 1 : 0;
                });
            },
            fillQuestionSample(questionIndex) {
                const question = this.questions[questionIndex];
                if (!question) return;
                const n = questionIndex + 1;
                question.description = `<p>${this.$t('admin.quiz.fillSampleStatement', { n })}</p>`;
                question.resolutionComment = this.$t('admin.quiz.fillSampleComment', { n });
                const samples = [
                    {
                        description: this.$t('admin.quiz.fillSampleCorrect', { n }),
                        correct: 1,
                    },
                    {
                        description: this.$t('admin.quiz.fillSampleWrong', { i: 1, n }),
                        correct: 0,
                    },
                    {
                        description: this.$t('admin.quiz.fillSampleWrong', { i: 2, n }),
                        correct: 0,
                    },
                    {
                        description: this.$t('admin.quiz.fillSampleWrong', { i: 3, n }),
                        correct: 0,
                    },
                ];
                if (!Array.isArray(question.answers)) {
                    question.answers = [];
                }
                samples.forEach((sample, answerIndex) => {
                    const current = question.answers[answerIndex];
                    if (current) {
                        current.description = sample.description;
                        current.correct = sample.correct;
                        return;
                    }
                    if (question.answers.length >= this.maxAnswers) return;
                    question.answers.push({
                        description: sample.description,
                        correct: sample.correct,
                    });
                });
                question.answers.forEach((answer, answerIndex) => {
                    if (answerIndex >= samples.length) answer.correct = 0;
                });
                this.openedQuestions[questionIndex] = true;
                this.activeQuestionIndex = questionIndex;
            },
            addNewAnswer(questionIndex) {
                if (this.maxAnswersReached(questionIndex)) return;
                const answers = this.questions[questionIndex].answers;
                answers.push({
                    description: '',
                    correct: 0,
                });
            },
            removeAnswer(questionIndex, answerIndex) {
                const answers = this.questions[questionIndex].answers;
                if (answers.length <= 1) return; // Prevent removing the last answer
                answers.splice(answerIndex, 1);
                // Ensure at least one answer is marked correct
                if (!answers.some((a) => a.correct)) {
                    answers[0].correct = 1;
                }
            },
            addNewQuestion() {
                if (this.maxQuestionsReached()) return;
                const questionsQuantity = this.questions.length;
                this.questions.push({
                    description: '',
                    resolutionComment: '',
                    order: questionsQuantity,
                    status: 1,
                    answers: [
                        {
                            description: '',
                            correct: 1, // Default first answer as correct
                        },
                    ],
                });
                // Open the newly added question
                this.openedQuestions.push(true);
                this.activeQuestionIndex = this.questions.length - 1;
            },
            async removeQuestion() {
                if (this.deleteQuestionIndex === null) return;
                try {
                    const questionPayload = {
                        ...this.questions[this.deleteQuestionIndex],
                        idQuiz: this.quiz.id,
                        status: 0,
                    };
                    if (this.questions[this.deleteQuestionIndex].id) {
                        await apiAnswer.editQuestionAnswer(
                            this.questions[this.deleteQuestionIndex].id,
                            questionPayload,
                            this.token
                        );
                    }
                    this.questions.splice(this.deleteQuestionIndex, 1);
                    this.openedQuestions.splice(this.deleteQuestionIndex, 1);
                    if (this.activeQuestionIndex >= this.questions.length) {
                        this.activeQuestionIndex = Math.max(this.questions.length - 1, 0);
                    } else if (this.deleteQuestionIndex < this.activeQuestionIndex) {
                        this.activeQuestionIndex -= 1;
                    }
                    this.deleteQuestionModal = false;
                    this.notify(this.$t('admin.quiz.questionRemoved'), 'success');
                } catch (error) {
                    this.notify(this.$t('admin.quiz.questionRemoveError'), 'error');
                    console.error(error);
                }
            },
            maxQuestionsReached() {
                return this.questions.length >= this.maxQuestions;
            },
            maxAnswersReached(questionIndex) {
                return (
                    this.questions[questionIndex].answers.length >=
                    this.maxAnswers
                );
            },
            validateQuiz() {
                this.fieldErrors.title = '';
                if (!this.quiz.title.trim()) {
                    const message = this.$t('admin.quiz.titleRequired');
                    this.fieldErrors.title = message;
                    this.notify(message, 'error');
                    return false;
                }
                return true;
            },
            validateQuestions() {
                this.fieldErrors.questions = [];
                if (!this.questions.length) {
                    this.notify(this.$t('admin.quiz.addQuestion'), 'error');
                    return false;
                }
                for (const [qIdx, q] of this.questions.entries()) {
                    const questionError = {
                        description: '',
                        answers: '',
                        correct: '',
                        items: {},
                    };
                    const fail = (field, message, itemIndex = null) => {
                        if (itemIndex === null) {
                            questionError[field] = message;
                        } else {
                            questionError.items[itemIndex] = message;
                        }
                        this.fieldErrors.questions[qIdx] = questionError;
                        this.openedQuestions[qIdx] = true;
                        this.activeQuestionIndex = qIdx;
                        this.notify(message, 'error');
                    };
                    if (
                        !q.description ||
                        !q.description.replace(/<[^>]+>/g, '').trim()
                    ) {
                        fail(
                            'description',
                            this.$t('admin.quiz.questionStatementRequired', { n: qIdx + 1 })
                        );
                        return false;
                    }
                    if (!q.answers || q.answers.length < 1) {
                        fail(
                            'answers',
                            this.$t('admin.quiz.questionNeedsAlternatives', { n: qIdx + 1 })
                        );
                        return false;
                    }
                    if (!q.answers.some((a) => a.correct)) {
                        fail(
                            'correct',
                            this.$t('admin.quiz.questionNeedsCorrect', { n: qIdx + 1 })
                        );
                        return false;
                    }
                    for (const [aIdx, a] of q.answers.entries()) {
                        if (!a.description.trim()) {
                            fail(
                                'items',
                                this.$t('admin.quiz.alternativeEmpty', { a: aIdx + 1, q: qIdx + 1 }),
                                aIdx
                            );
                            return false;
                        }
                    }
                }
                return true;
            },
            async saveAll() {
                if (this.isLoading || this.isSaving) return;

                try {
                    this.isSaving = true;
                    this.fieldErrors = { title: '', questions: [] };
                    if (!this.validateQuiz() || !this.validateQuestions()) {
                        return;
                    }

                    await this.saveQuiz();
                    await this.saveQuestions();
                    await this.saveLesson();

                    this.saveInitialState();
                    this.notify(this.$t('admin.quiz.saveSuccess'), 'success');
                    

                } catch (error) {
                    console.error('Error saving quiz data:', error);
                    this.notify(
                        this.$t('admin.quiz.saveError'),
                        'error'
                    );
                } finally {
                    this.isSaving = false;
                }
            },
            async saveQuiz() {
                const quizPayload = {
                    ...this.quiz,
                    status: 1,
                };
                let response;
                if (this.quiz.id) {
                    response = await apiQuiz.editQuiz(
                        this.quiz.id,
                        quizPayload,
                        this.token
                    );
                } else {
                    response = await apiQuiz.createNewQuiz(
                        quizPayload,
                        this.token
                    );
                }
                if (response) {
                    this.quiz = response;
                }
            },
            async saveQuestions() {
                const questionsPayload = this.questions.map((q, index) => {
                    const question = { ...q };
                    delete question.resolutionComment;
                    return {
                        ...question,
                        order: index + 1,
                        idQuiz: this.quiz.id,
                    };
                });

                const questionsToEdit = questionsPayload.filter((q) => q.id);
                const questionsToCreate = questionsPayload.filter((q) => !q.id);

                let createdQuestions = [];
                let editedQuestions = [];

                if (questionsToEdit.length > 0) {
                    const response = await apiAnswer.editMultiQuestionAnswer(
                        questionsToEdit,
                        this.token
                    );
                    if (Array.isArray(response)) {
                        editedQuestions = response;
                    }
                }

                if (questionsToCreate.length > 0) {
                    const response = await apiAnswer.createMultiQuestionAnswer(
                        questionsToCreate,
                        this.token
                    );
                    if (Array.isArray(response)) {
                        createdQuestions = response;
                    }
                }

                const updatedQuestions = [
                    ...editedQuestions,
                    ...createdQuestions,
                ];
                if (updatedQuestions.length > 0) {
                    this.questions = updatedQuestions
                        .sort((a, b) => a.order - b.order)
                        .map((q) => ({
                            ...q,
                            answers: Array.isArray(q.answers) ? q.answers : [],
                        }));
                }
            },
            async saveLesson() {
                const commonPayload = {
                    title: this.quiz.title,
                    status: 1,
                    idQuiz: this.quiz.id,
                };

                const lessonPayload = this.isEditing
                    ? {
                          ...this.lessonData,
                          ...commonPayload,
                          idLesson: this.lessonId,
                      }
                    : {
                          ...commonPayload,
                          description: this.quiz.description || '',
                          type: 5,
                          idModule: this.currentModule?.id,
                          idCourse: this.currentCollections?.id,
                      };

                let response;
                if (this.lessonId) {
                    response = await apiLesson.editLesson(
                        this.token,
                        lessonPayload,
                        this.lessonId
                    );
                } else {
                    response = await apiLesson.createNewLesson(
                        lessonPayload,
                        this.token
                    );
                }

                if (response) {
                    this.lessonData = response;
                }
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                    persist: true,
                });
            },
            openDeleteQuestionModal(index) {
                this.deleteQuestionIndex = index;
                this.deleteQuestionModal = true;
            },
            saveInitialState() {
                this.initialFormState = {
                    quiz: JSON.parse(JSON.stringify(this.quiz)),
                    requiredScore: this.requiredScore,
                    approvalPercentage: this.approvalPercentage,
                    questions: JSON.parse(JSON.stringify(this.questions)),
                };
                this.hasUnsavedChanges = false;
            },

            checkForChanges() {
                if (!this.initialFormState || !this.isInitialized) return;

                const currentState = {
                    quiz: JSON.parse(JSON.stringify(this.quiz)),
                    requiredScore: this.requiredScore,
                    approvalPercentage: this.approvalPercentage,
                    questions: JSON.parse(JSON.stringify(this.questions)),
                };

                this.hasUnsavedChanges =
                    JSON.stringify(currentState) !==
                    JSON.stringify(this.initialFormState);
            },

            openCancelModal() {
                if (this.hasUnsavedChanges) {
                    this.cancelModal = true;
                } else {
                    this.$router.push('/dashboard/curso');
                }
            },
        },
    };
</script>

<style src="@/css/weeduc/admin/quiz-editor.css"></style>
